import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatCard, rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import InvoiceView from "../components/InvoiceView";
import { toast } from "sonner";
import { IndianRupee, Receipt, Download, FileText, Printer, Search, ChevronLeft, Eye, Pencil, History, Trash2 } from "lucide-react";
import { ReportDateFilter, useReportRange } from "../components/ReportDateFilter";

const prettyDate = (s) => { if (!s) return ""; const [y, m, d] = s.split("-"); return new Date(y, m - 1, d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); };

function toCsv(name, headerLines, head, rows) {
  const body = [...headerLines.map((l) => [l]), head, ...rows].map((r) => r.map((c) => `"${String(c ?? "")}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([body], { type: "text/csv" }));
  a.download = `${name}.csv`; a.click();
}

export default function BillReport({ onBack }) {
  const { branchParam } = useBranch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ["OWNER", "SUPER_ADMIN"].includes(user?.role) || !!(user?.permissions || {}).edit_bill;
  const canDelete = ["OWNER", "SUPER_ADMIN"].includes(user?.role) || !!(user?.permissions || {}).delete_bill;
  const [delRow, setDelRow] = useState(null);
  const [delReason, setDelReason] = useState("");
  const [deleting, setDeleting] = useState(false);
  const doDelete = async () => {
    if (!delRow) return;
    setDeleting(true);
    try {
      await api.delete(`/invoices/${delRow.id}`, { params: { reason: delReason } });
      toast.success(`Bill ${delRow.invoice_no} deleted`);
      setDelRow(null); setDelReason(""); loadBills();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };
  const [histRow, setHistRow] = useState(null);
  const [hist, setHist] = useState([]);
  const openHistory = async (r) => {
    setHistRow(r); setHist([]);
    try { const { data } = await api.get(`/invoices/${r.id}/history`); setHist(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const rr = useReportRange("today");
  const { start, end } = rr;
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [staffId, setStaffId] = useState("all");
  const [pmFilter, setPmFilter] = useState("all");
  const [viewId, setViewId] = useState(null);

  useEffect(() => {
    api.get("/staff", { params: { branch_id: branchParam } }).then((r) => setStaffList(r.data)).catch(() => {});
  }, [branchParam]);

  const loadBills = () => api.get("/reports/bills", { params: { start, end, branch_id: branchParam, staff_id: staffId } }).then((r) => setData(r.data));
  useEffect(() => {
    setData(null);
    loadBills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end, branchParam, staffId]);

  const rows = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    return data.rows.filter((r) => {
      if (pmFilter !== "all" && r.payment_mode !== pmFilter) return false;
      if (!term) return true;
      return (r.invoice_no || "").toLowerCase().includes(term) ||
        (r.client_name || "").toLowerCase().includes(term) ||
        (r.mobile || "").toLowerCase().includes(term);
    });
  }, [data, q, pmFilter]);

  const showDaily = rr.mode !== "today";

  const csvExport = () => {
    if (!data) return;
    const hdr = [`Bill Report`, `Branch: ${data.branch_name}`, `Range: ${prettyDate(start)} to ${prettyDate(end)}`, ""];
    toCsv("bill_report",
      hdr,
      ["Invoice No.", "Date", "Time", "Client Name", "Contact Number", "Services", "Staff Name", "Payment Mode", "Total Amount"],
      [...rows.map((r) => [r.invoice_no, r.date, r.time, r.client_name, r.mobile, r.services, r.staff, r.payment_mode === "Split" ? `Split (${r.payment_breakdown})` : r.payment_mode, r.total]),
       ["TOTAL", "", "", "", "", "", "", data.totals.bills + " bills", data.totals.business]]);
  };

  const th = "text-muted-foreground text-left border-b border-border";

  return (
    <div>
      <button onClick={onBack} data-testid="report-back" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3"><ChevronLeft size={16} /> All Reports</button>
      <PageHeader title="Bill Report" subtitle="Every completed bill with services, staff, payment mode and total"
        actions={
          <div className="flex gap-2">
            <Button onClick={csvExport} variant="outline" className="rounded-sm" data-testid="bill-export-csv"><Download size={15} className="mr-1" /> Excel/CSV</Button>
            <Button onClick={() => window.print()} variant="outline" className="rounded-sm" data-testid="bill-export-pdf"><FileText size={15} className="mr-1" /> PDF</Button>
            <Button onClick={() => window.print()} variant="outline" className="rounded-sm" data-testid="bill-print"><Printer size={15} className="mr-1" /> Print</Button>
          </div>
        } />

      <Card className="p-4 bg-card border-border mb-6">
        <Badge variant="outline" className="border-primary text-primary mb-3" data-testid="bill-branch-chip">Showing: {data?.branch_name || "…"}</Badge>
        <ReportDateFilter r={rr} prefix="bill-filter" />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Staff:</span>
          <Select value={staffId} onValueChange={setStaffId}>
            <SelectTrigger className="bg-secondary w-56" data-testid="bill-staff-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Staff</SelectItem>
              {staffList.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground ml-2">Payment:</span>
          <Select value={pmFilter} onValueChange={setPmFilter}>
            <SelectTrigger className="bg-secondary w-44" data-testid="bill-payment-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Modes</SelectItem>
              {["Cash", "UPI", "Card", "Wallet", "Cashback", "Bank Transfer", "Split"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {!data ? <p className="text-muted-foreground text-sm">Loading…</p> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="Total Bills" value={data.totals.bills} icon={Receipt} testid="bill-kpi-bills" />
            <StatCard label="Total Business" value={rupee(data.totals.business)} icon={IndianRupee} testid="bill-kpi-business" />
          </div>

          <div className="relative mb-4 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice / client / mobile…" data-testid="bill-search" className="bg-secondary pl-9" />
          </div>

          <Card className="p-0 bg-card border-border overflow-x-auto mb-6" data-testid="bill-table">
            <table className="w-full text-sm whitespace-nowrap">
              <thead><tr className={th}>
                <th className="py-3 px-4">Invoice No.</th><th className="px-2">Date</th><th className="px-2">Time</th>
                <th className="px-2">Client Name</th><th className="px-2">Contact</th><th className="px-2">Services</th>
                <th className="px-2">Staff</th><th className="px-2">Payment</th><th className="px-2">Status</th><th className="px-2 text-center w-10">View</th><th className="text-right px-4">Total</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.invoice_no} className="border-b border-border/50 hover:bg-secondary/30" data-testid={`bill-row-${r.invoice_no}`}>
                    <td className="py-2.5 px-4 font-medium text-primary">{r.invoice_no}</td>
                    <td className="px-2">{r.date}</td>
                    <td className="px-2 text-muted-foreground">{r.time}</td>
                    <td className="px-2">{r.client_name}</td>
                    <td className="px-2 text-muted-foreground">{r.mobile}</td>
                    <td className="px-2 max-w-[240px] whitespace-normal">{r.services}</td>
                    <td className="px-2">{r.staff}</td>
                    <td className="px-2">
                      {r.payment_mode === "Split"
                        ? <Badge variant="outline" className="border-amber-400/40 text-amber-300 cursor-help" title={r.payment_breakdown}>Split</Badge>
                        : <span>{r.payment_mode}</span>}
                    </td>
                    <td className="px-2"><Badge variant="outline" className="text-green-300 border-green-800">Paid</Badge></td>
                    <td className="px-2 text-center">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-sm"
                        onClick={() => setViewId(r.id)} data-testid={`bill-view-${r.invoice_no}`} title="View invoice"><Eye size={16} /></Button>
                      {canEdit && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-sm"
                          onClick={() => navigate("/billing/new", { state: { editId: r.id } })} data-testid={`bill-edit-${r.invoice_no}`} title="Edit invoice"><Pencil size={15} /></Button>
                      )}
                      {canEdit && r.edited_at && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-sm"
                          onClick={() => openHistory(r)} data-testid={`bill-history-${r.invoice_no}`} title="Edit history"><History size={15} /></Button>
                      )}
                      {canDelete && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-sm"
                          onClick={() => setDelRow(r)} data-testid={`bill-delete-${r.invoice_no}`} title="Delete bill"><Trash2 size={15} /></Button>
                      )}
                    </td>
                    <td className="text-right px-4 text-primary font-medium">{rupee(r.total)}</td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={11} className="text-center text-muted-foreground py-8">No completed bills in this range.</td></tr>}
              </tbody>
              {!!rows.length && (
                <tfoot><tr className="bg-secondary/40 font-semibold border-t border-border" data-testid="bill-total-row">
                  <td className="py-3 px-4" colSpan={10}>TOTAL BILLS: {data.totals.bills}</td>
                  <td className="text-right px-4 text-primary">TOTAL BUSINESS: {rupee(data.totals.business)}</td>
                </tr></tfoot>
              )}
            </table>
          </Card>

          {showDaily && (
            <Card className="p-5 bg-card border-border" data-testid="bill-daily-summary">
              <h4 className="font-display text-lg mb-3">Date-wise Summary</h4>
              <div className="space-y-1">
                {data.daily.map((d) => (
                  <div key={d.date} className="flex justify-between text-sm border-b border-border/40 py-2" data-testid={`bill-daysum-${d.date}`}>
                    <span>{prettyDate(d.date)}</span>
                    <span className="text-muted-foreground">Bills: <span className="text-foreground">{d.bills}</span> &nbsp;·&nbsp; Business: <span className="text-primary">{rupee(d.business)}</span></span>
                  </div>
                ))}
                {!data.daily.length && <p className="text-muted-foreground text-sm">No data in this range.</p>}
                <div className="flex justify-between text-sm font-semibold pt-3">
                  <span>TOTAL BILLS: {data.totals.bills}</span>
                  <span>TOTAL BUSINESS: <span className="text-primary">{rupee(data.totals.business)}</span></span>
                </div>
              </div>
            </Card>
          )}
        </>
      )}

      <Dialog open={!!viewId} onOpenChange={(o) => !o && setViewId(null)}>
        <DialogContent className="bg-white border-border max-w-2xl max-h-[92vh] overflow-y-auto p-0" data-testid="bill-invoice-view">
          <DialogHeader className="sr-only"><DialogTitle>Invoice</DialogTitle></DialogHeader>
          {viewId && <InvoiceView id={viewId} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!delRow} onOpenChange={(o) => !o && (setDelRow(null), setDelReason(""))}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="bill-delete-dialog">
          <DialogHeader><DialogTitle>Delete Bill</DialogTitle></DialogHeader>
          <p className="text-sm">Are you sure you want to delete this bill?</p>
          <p className="text-xs text-muted-foreground -mt-1">Bill <span className="text-foreground font-medium">{delRow?.invoice_no}</span> · ₹{Number(delRow?.total || delRow?.grand_total || 0).toLocaleString("en-IN")}. This reverses stock, incentives and customer spend, and cannot be undone.</p>
          <div>
            <label className="text-xs text-muted-foreground">Reason for deletion</label>
            <textarea value={delReason} onChange={(e) => setDelReason(e.target.value)} rows={2} placeholder="e.g. wrong bill, duplicate, test entry" className="mt-1 w-full rounded-sm bg-secondary border border-border p-2 text-sm" data-testid="bill-delete-reason" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => (setDelRow(null), setDelReason(""))} data-testid="bill-delete-cancel">Cancel</Button>
            <Button className="rounded-sm bg-destructive text-white hover:bg-destructive/90" disabled={deleting} onClick={doDelete} data-testid="bill-delete-confirm">{deleting ? "Deleting…" : "Delete Bill"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!histRow} onOpenChange={(o) => !o && setHistRow(null)}>
        <DialogContent className="bg-card border-border max-w-lg" data-testid="bill-history-dialog">
          <DialogHeader><DialogTitle>Edit history — {histRow?.invoice_no}</DialogTitle></DialogHeader>
          {histRow?.last_edit_reason && <p className="text-xs text-muted-foreground -mt-1">Last reason: <span className="text-foreground">{histRow.last_edit_reason}</span></p>}
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {hist.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center" data-testid="bill-history-empty">No edit history for this bill.</p>}
            {hist.map((h, i) => {
              const ov = h.old_value || {}; const nv = h.new_value || {};
              const changes = [];
              if (ov.grand_total !== nv.grand_total) changes.push(`Total ₹${ov.grand_total ?? "?"} → ₹${nv.grand_total ?? "?"}`);
              if (ov.discount !== nv.discount) changes.push(`Discount ₹${ov.discount ?? 0} → ₹${nv.discount ?? 0}`);
              if (ov.bill_date !== nv.bill_date) changes.push(`Date ${ov.bill_date || "?"} → ${nv.bill_date || "?"}`);
              if ((ov.services?.length || 0) !== (nv.services?.length || 0)) changes.push(`Services ${ov.services?.length || 0} → ${nv.services?.length || 0} lines`);
              return (
                <div key={i} className="rounded-md border border-border p-3 text-sm" data-testid={`bill-history-row-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium capitalize">{h.action}</span>
                    <span className="text-xs text-muted-foreground">{h.timestamp?.replace("T", " ").slice(0, 19)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">by {h.user_name || "—"} ({h.user_role || "—"})</p>
                  {changes.length > 0 && <ul className="mt-1 text-xs list-disc list-inside text-foreground/90">{changes.map((c, k) => <li key={k}>{c}</li>)}</ul>}
                  {h.reason && <p className="text-xs mt-1 text-primary">Reason: {h.reason}</p>}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
